import { useSelector, useDispatch } from "react-redux";
import { map, filter } from "lodash";
import PropTypes from "prop-types";

import { getReferralWorklistFilter,
  getTotalNumberOfReferral } from "app/main/referralWorklist/reducers/referralWorklist.reducers";
import { getReferralWorkflowStateSettings, getEnabledTriageCategorySettings, getReportingUseHangfireQueuing } from "app/auth/store/reducers/system-configuration";
import { updateReferralWorkListFilterTags } from "app/main/referralWorklist/actions/referralWorklist.actions";
import { handleHangfireExportResponse } from "app/main/reportExport/actions/reportExport.actions";
import { showMessage } from "app/store/actions";
import useReferralFieldSettings from "../../referrals/hooks/useReferralFieldSettings";

const useReferralSearch = ({
  params,
  orgUnitId,
  searchAPI,
  exportAPI,
  updateFilterTags = updateReferralWorkListFilterTags,
}) => {
  const dispatch = useDispatch();
  const triageCategorySettings = useSelector(getEnabledTriageCategorySettings);
  const triageCategoryChoices = map(triageCategorySettings, x => x.triageCategory);
  const referralWorkflowSettings = useSelector(getReferralWorkflowStateSettings);
  const referralStatusChoices = map(filter(referralWorkflowSettings, x => x.isEnabled === true), setting => setting.workflowState);
  const presetFilter = useSelector(getReferralWorklistFilter);
  const reportingUseHangfireQueuing = useSelector(getReportingUseHangfireQueuing);
  const totalNumberOfReferrals = useSelector(getTotalNumberOfReferral);
  const { fieldLabels: referralFieldLabels } = useReferralFieldSettings();

  const getParameters = parameters => {
    if (!parameters) {
      return {
        searchParams: null,
        initialValues: null,
      };
    }

    const referralFilters = {
      ...parameters,
      orgUnitId,
      dateRange: [params.startDate, params.endDate],
    };

    return ({
      searchParams: referralFilters,
      orgUnitId,
      initialValues: {
        referralFilters,
      },
    });
  };

  const onSearch = (newParams, page, pageSize, reload = false) => new Promise(resolve => {
    const { searchParams } = getParameters(newParams);

    setTimeout(() => {
      resolve(dispatch(searchAPI(searchParams, page || 1, pageSize || 14, reload))
        .then(res => {
          if (res) {
            dispatch(updateFilterTags(res.payload?.searchParams, referralFieldLabels));
          }
        }));
    }, 500);
  });

  const onExport = async () => {
    if (!exportAPI) return null;

    const { searchParams } = getParameters(params);

    try {
      /*
       * background exporting: false
       */
      if (!reportingUseHangfireQueuing) {
        const response = await dispatch(exportAPI(searchParams, false, totalNumberOfReferrals));

        if (!response.ok) {
          const errorMessage = await response.text();
          dispatch(showMessage({ message: errorMessage, variant: "error" }));
          return null;
        }
        return null;
      }

      /*
       * background exporting: true
       */
      return handleHangfireExportResponse(
        dispatch(exportAPI(searchParams, true, totalNumberOfReferrals)),
        dispatch,
      );
    } catch (error) {
      dispatch(showMessage({ message: error, variant: "error" }));
      return null;
    }
  };

  const { searchParams, initialValues } = getParameters(params);

  return ({
    searchParams,
    initialValues,
    referralStatusChoices,
    triageCategoryChoices,
    presetFilter,
    referralWorkflowSettings,
    triageCategorySettings,
    onSearch,
    onExport,
  });
};

useReferralSearch.propTypes = {
  params: PropTypes.shape({}).isRequired, // params is an object
  searchAPI: PropTypes.func.isRequired,
  exportAPI: PropTypes.func,
};

export default useReferralSearch;
